import {
  ChangeEvent,
  SyntheticEvent,
  useContext,
  useEffect,
  useState,
} from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { defaultInvalidateQueryFilters, queryKeys } from "../../api/query";
import { useCreateInteraction } from "../../api/queries/interactions/useCreateInteraction";
import {
  CreateInteractionPayload,
  InteractionType,
  // UpdateInteractionPayload,
} from "../../api/types/interaction";
// import { useUpdateInteraction } from "../../api/queries/interactions/useUpdateInteraction";
import { useDeleteInteraction } from "../../api/queries/interactions/useDeleteInteraction";
import { useInteraction } from "../../api/queries/interactions/useInteraction";
import { InputNumber, InputNumberChangeEvent } from "primereact/inputnumber";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { Calendar } from "primereact/calendar";
import moment from "moment";
import { Chips, ChipsChangeEvent } from "primereact/chips";
import commonStyles from "../common.module.css";
import { Representative } from "../../api/types/representative";
import { Editor, defaultEditorModules } from "../Editor";
import { useInteractionTypes } from "../../api/queries/interactions/useInteractionTypes";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { useRepresentatives } from "../../api/queries/representatives/useRepresentatives";
import { useUsers } from "../../api/queries/user/useUsers";
import { UserListItem } from "../../api/types/user";
import CreatableSelect from "react-select/creatable";
import { userContext } from "../../context/user";
import { FormEvent, Nullable } from "primereact/ts-helpers";

export default function InteractionForm() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = useContext(userContext);

  const createInteraction = useCreateInteraction();
  const deleteInteraction = useDeleteInteraction();
  // const updateInteraction = useUpdateInteraction(uuid);
  const { data: interactionResponse } = useInteraction(uuid);
  const { data: interactionTypeOptions } = useInteractionTypes();
  const { data: representativeOptions } = useRepresentatives({
    limit: 100,
    offset: 0,
  });
  const { data: userOptions } = useUsers(
    {
      limit: 100,
      offset: 0,
    },
    isAdmin(),
  );

  const [name, setName] = useState<string>("");
  const onNameChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setName(event.target.value);

  const [description, setDescription] = useState<string>("");
  const onDescriptionChange = (value: string): void => setDescription(value);

  const [timeSpent, setTimeSpent] = useState<number | null>(null);
  const onTimeSpentChange = (event: InputNumberChangeEvent): void =>
    setTimeSpent(event.value);

  const [completed, setCompleted] = useState<boolean>(false);
  const onCompletedChange = (event: CheckboxChangeEvent) =>
    setCompleted(event.checked || false);

  const [dueDate, setDueDate] = useState<Nullable<Date>>(null);
  const onDueDateChange = (event: FormEvent<Date>) =>
    setDueDate(event.value || null);

  const [interactionTypes, setInteractionTypes] = useState<InteractionType[]>(
    [],
  );

  const onInteractionTypesChange = (options: any[] | null) => {
    const types = options?.map((option) => {
      if (option.__isNew__) {
        return {
          id: option.value,
          name: option.value,
        } as InteractionType;
      }

      return option;
    });

    setInteractionTypes(types || []);
  };

  const [users, setUsers] = useState<UserListItem[]>([]);
  const onUsersChange = (event: MultiSelectChangeEvent) =>
    setUsers(event.value);

  const tUser = (item: UserListItem) => `${item.email}`;

  const tSelectedUser = (item: UserListItem) =>
    item ? `${item.email}, ` : <span>&nbsp;</span>;

  const [representatives, setRepresentatives] = useState<Representative[]>([]);
  const onRepresentativesChange = (event: MultiSelectChangeEvent): void =>
    setRepresentatives(event.value);

  const tRepresentative = (item: Representative) =>
    `${item.last_name} ${item.first_name}`;

  const tSelectedRepresentative = (item: Representative) =>
    item ? `${item.last_name} ${item.first_name}, ` : <span>&nbsp;</span>;

  const [involvedPeople, setInvolvedPeople] = useState<string[]>([]);
  const onInvolvedPeopleChange = (event: ChipsChangeEvent) =>
    setInvolvedPeople(event.value || []);

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    const mDueDate = moment(dueDate?.toString());

    if (uuid) {
      // const payload: UpdateInteractionPayload = {
      //   name,
      //   description,
      //   time_spent: timeSpent || 0,
      //   completed,
      //   due_date: mDueDate.isValid() ? mDueDate.format("YYYY-MM-DD") : "",
      //   interaction_types: interactionTypes.map((type) => type.name),
      //   involved_users: users.map((user) => user.id),
      //   involved_representatives: representatives.map(
      //     (representative) => representative.id
      //   ),
      //   involved_people: involvedPeople,
      //   timezone_offset: 0,
      // };
      // await updateInteraction.mutateAsync(payload);
      // queryClient.invalidateQueries(queryKeys.interaction(uuid));
    } else {
      const payload: CreateInteractionPayload = {
        name,
        description,
        time_spent: timeSpent || 0,
        completed,
        due_date: mDueDate.isValid() ? mDueDate.format("YYYY-MM-DD") : "",
        interaction_types: interactionTypes.map((type) => type.name),
        involved_users: users.map((user) => user.id),
        involved_representatives: representatives.map(
          (representative) => representative.id,
        ),
        involved_people: involvedPeople,
      };

      await createInteraction.mutateAsync(payload);
    }

    queryClient.invalidateQueries(
      queryKeys.interactionList(),
      defaultInvalidateQueryFilters,
    );

    queryClient.invalidateQueries(
      queryKeys.interactionTypes(),
      defaultInvalidateQueryFilters,
    );

    navigate("/interactions");
  };

  const handleDelete = async () => {
    await deleteInteraction.mutateAsync(uuid);

    queryClient.invalidateQueries(queryKeys.interaction(uuid));
    queryClient.invalidateQueries(queryKeys.interactionList(), {
      refetchInactive: true,
    });

    navigate("/interactions");
  };

  useEffect(() => {
    if (interactionResponse) {
      const interaction = interactionResponse.data;

      setName(interaction.name);
      setDescription(interaction.description);
      setTimeSpent(interaction.time_spent);
      setCompleted(interaction.completed);
      setDueDate(moment(interaction.due_date).toDate());
      setInteractionTypes(interaction.interaction_types);

      setRepresentatives(
        interaction.people_involved
          .filter((people) => people.representative !== null)
          .map((people) => people.representative) as Representative[],
      );

      setInvolvedPeople(
        interaction.people_involved
          .filter((people) => people.other !== null)
          .map((people) => people.other) as string[],
      );
    }
  }, [interactionResponse]);

  return (
    <form onSubmit={handleSubmit} className="col-12">
      <div className="p-fluid">
        <div className="field">
          <label>Name:</label>
          <InputText value={name} onChange={onNameChange} />
        </div>

        <div className="field">
          <label>Description:</label>
          <Editor
            content={description}
            onChange={onDescriptionChange}
            modules={[...defaultEditorModules, "formula"]}
          />
        </div>

        <div className="field">
          <label>Spent time (min):</label>
          <InputNumber value={timeSpent} onChange={onTimeSpentChange} />
        </div>

        <div className="field-checkbox">
          <Checkbox
            checked={completed}
            onChange={onCompletedChange}
            inputId="completed"
          />
          <label htmlFor="completed">Completed</label>
        </div>

        <div className="field">
          <label>Due date:</label>
          <Calendar value={dueDate} onChange={onDueDateChange} />
        </div>

        <div className="field">
          <label>Interaction types:</label>
          <CreatableSelect
            isMulti={true}
            options={interactionTypeOptions?.data.results || []}
            value={interactionTypes}
            placeholder=""
            formatCreateLabel={(inputValue: string) =>
              `New type: ${inputValue}`
            }
            // @ts-ignore
            onChange={onInteractionTypesChange}
            getOptionLabel={(type: any) => type.name || type.label}
            getOptionValue={(type: any) => type.id || type.value}
          />
        </div>

        <div className="field">
          <label>Involved users:</label>
          <MultiSelect
            options={userOptions?.data.results || []}
            value={users}
            itemTemplate={tUser}
            selectedItemTemplate={tSelectedUser}
            onChange={onUsersChange}
          />
        </div>

        <div className="field">
          <label>Involved representatives:</label>
          <MultiSelect
            options={representativeOptions?.data.results || []}
            value={representatives}
            itemTemplate={tRepresentative}
            selectedItemTemplate={tSelectedRepresentative}
            onChange={onRepresentativesChange}
            // @ts-ignore
            emptyMessage={""}
          />
        </div>

        <div className="field">
          <label>Involved people:</label>
          <Chips
            value={involvedPeople}
            onChange={onInvolvedPeopleChange}
            separator=","
          />
          <small className={commonStyles.fieldHelp}>
            Separate multiple values with comma.
          </small>
        </div>
      </div>

      <div className="flex gap-1 justify-content-end">
        {uuid && (
          <Button
            type="button"
            label="Remove"
            outlined={true}
            className="p-button-sm p-button-danger"
            onClick={handleDelete}
          />
        )}

        <Button
          type="submit"
          label="Save"
          className="p-button-main p-button-sm"
        />
      </div>
    </form>
  );
}
